"use client";

import { ChevronDownIcon } from "@vetty/icons";
import classNames from "classnames";
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { useAppNav } from "../../context/AppNavContext";
import type { AppSidebarSubItem } from "../../types";
import styles from "./SidebarNavItem.module.scss";

const NavTarget = ({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) => {
  const { renderLink, onNavLinkClick, onNavigate } = useAppNav();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    onNavLinkClick?.(href, event);
    if (onClick) onClick();
    else if (!renderLink) onNavigate?.(href);
  };

  if (onClick && !renderLink) {
    return (
      <button type="button" className={className} onClick={handleClick}>
        {children}
      </button>
    );
  }

  if (renderLink) {
    return (
      <>
        {renderLink({
          href,
          className,
          children,
          onClick: handleClick,
        })}
      </>
    );
  }

  return (
    <button type="button" className={className} onClick={handleClick}>
      {children}
    </button>
  );
};

export const SidebarNavItem = ({
  icon,
  label,
  href,
  subItems,
  excludeActivePathPrefixes,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  href: string;
  subItems?: AppSidebarSubItem[];
  excludeActivePathPrefixes?: string[];
  onClick?: () => void;
}) => {
  const { pathname } = useAppNav();
  const hasSubItems = Boolean(subItems?.length);

  const routeBelongsToThisParent = Boolean(
    hasSubItems &&
      (pathname === href ||
        pathname.startsWith(`${href}/`) ||
        subItems!.some(
          (item) =>
            !item.external &&
            (pathname === item.href || pathname.startsWith(`${item.href}/`))
        ))
  );

  const [isExpanded, setIsExpanded] = useState(routeBelongsToThisParent);

  useEffect(() => {
    if (!hasSubItems) return;
    setIsExpanded(routeBelongsToThisParent);
  }, [hasSubItems, pathname, href, routeBelongsToThisParent]);

  const isExcludedBySibling =
    excludeActivePathPrefixes?.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    ) ?? false;

  const isActive = hasSubItems
    ? pathname === href
    : (pathname === href || pathname.startsWith(`${href}/`)) &&
      !isExcludedBySibling;

  if (hasSubItems) {
    return (
      <div className={styles["comp-wrapper"]}>
        <div
          className={classNames(
            styles["comp"],
            styles["comp--parent"],
            isActive && styles["comp--active"]
          )}
        >
          <NavTarget href={href} className={styles["comp__link"]}>
            <div className={styles["comp__icon"]}>{icon}</div>
            <div className={styles["comp__label"]}>{label}</div>
          </NavTarget>
          <button
            type="button"
            className={styles["comp__chevron"]}
            onClick={() => setIsExpanded((open) => !open)}
            aria-expanded={isExpanded}
          >
            <ChevronDownIcon
              style={{ width: 20, height: 20 }}
              className={classNames(
                styles["comp__chevron-icon"],
                isExpanded && styles["comp__chevron-icon--open"]
              )}
            />
          </button>
        </div>
        {isExpanded && (
          <div className={styles["comp__sub-items"]}>
            {subItems!.map((subItem) => {
              const isExternal = Boolean(subItem.external);
              const isSubActive = isExternal
                ? false
                : subItem.exactActive
                  ? pathname === subItem.href
                  : pathname === subItem.href ||
                    pathname.startsWith(`${subItem.href}/`);
              const subClass = classNames(
                styles["comp"],
                styles["comp--sub"],
                isSubActive && styles["comp--active"]
              );
              const subContent = (
                <>
                  <div className={styles["comp__icon"]}>{subItem.icon}</div>
                  <div className={styles["comp__label"]}>{subItem.label}</div>
                </>
              );
              if (isExternal) {
                return (
                  <a
                    key={subItem.href}
                    href={subItem.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={subClass}
                  >
                    {subContent}
                  </a>
                );
              }
              return (
                <NavTarget
                  key={subItem.href}
                  href={subItem.href}
                  className={subClass}
                >
                  {subContent}
                </NavTarget>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          styles["comp"],
          styles["comp--button"],
          isActive && styles["comp--active"]
        )}
      >
        <div className={styles["comp__icon"]}>{icon}</div>
        <div className={styles["comp__label"]}>{label}</div>
      </button>
    );
  }

  return (
    <NavTarget
      href={href}
      className={classNames(styles["comp"], isActive && styles["comp--active"])}
    >
      <div className={styles["comp__icon"]}>{icon}</div>
      <div className={styles["comp__label"]}>{label}</div>
    </NavTarget>
  );
};
